import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  Store,
  Tag,
  Truck,
  Wallet,
  BarChart3,
  Users,
  Megaphone,
  Settings,
  Menu,
  MessageSquare,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/atoms/button';
import { ScrollArea } from '@/components/atoms/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger, SheetClose } from '@/components/molecules/sheet';
import { useUIStore } from '@/lib/stores/ui.store';

const navigation = [
  { name: 'dashboard', href: '/', icon: LayoutDashboard },
  { name: 'orders', href: '/orders', icon: ShoppingCart },
  { name: 'messages', href: '/messages', icon: MessageSquare },
  { name: 'products', href: '/products', icon: Package },
  { name: 'shops', href: '/shops', icon: Store },
  { name: 'promotions', href: '/promotions', icon: Tag },
  { name: 'shipping', href: '/shipping', icon: Truck },
  { name: 'wallet', href: '/wallet', icon: Wallet },
  { name: 'metrics', href: '/metrics', icon: BarChart3 },
  { name: 'employees', href: '/employees', icon: Users },
  { name: 'ads', href: '/ads', icon: Megaphone },
  { name: 'settings', href: '/settings', icon: Settings },
];

export function MobileNav() {
  const location = useLocation();
  const pathname = location.pathname;
  const { t } = useTranslation();
  const { sidebarMobileOpen, setSidebarMobileOpen } = useUIStore();

  const isActive = (href: string) => {
    if (href === '/') {
      return pathname === '/';
    }
    return pathname.startsWith(href);
  };

  return (
    <Sheet open={sidebarMobileOpen} onOpenChange={setSidebarMobileOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="lg:hidden">
          <Menu className="size-5" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="p-0 w-72">
        <SheetHeader className="p-4 border-b">
          <SheetTitle className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-lg">V</span>
            </div>
            <span>Volovolo Pro</span>
          </SheetTitle>
          <SheetDescription className="sr-only">
            {t('navigation.menuDescription')}
          </SheetDescription>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-80px)]">
          <nav className="p-2 space-y-1">
            {navigation.map((item) => {
              const active = isActive(item.href);
              return (
                <SheetClose asChild key={item.name}>
                  <Link
                    to={item.href}
                    className={cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                      active
                        ? 'bg-accent text-accent-foreground'
                        : 'text-foreground hover:bg-accent/50 hover:text-accent-foreground'
                    )}
                  >
                    <item.icon className="size-5 shrink-0" />
                    <span>{t(`navigation.${item.name}`)}</span>
                  </Link>
                </SheetClose>
              );
            })}
          </nav>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
